import { useState, useEffect } from 'react';
import './AnalyticsTab.css';

//  Lit les trades du bot depuis localStorage
const loadBotTrades = () => {
  try {
    const saved = localStorage.getItem('bot-trades');
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

//  Structure d'un trade bot :
// { id, symbol, mint, entreeTs, sortieTs, achatUsd, venteUsd, pnlUsd, roi, roiPos, resultat, walletSuivi, txHash }

export default function AnalyticsTab() {
  const [period, setPeriod] = useState('30D');
  const [sortBy, setSortBy] = useState('date');
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    setTrades(loadBotTrades());
    // Écoute les mises à jour du bot
    const interval = setInterval(() => setTrades(loadBotTrades()), 5000);
    return () => clearInterval(interval);
  }, []);

  // Filtrer par période
  const now = Date.now() / 1000;
  const periodeMap = { '7D': 7*86400, '30D': 30*86400, '90D': 90*86400, 'ALL': Infinity };
  const filtered = trades.filter(t => {
    const age = now - (t.entreeTs || 0);
    return age <= (periodeMap[period] || Infinity);
  });

  // Stats globales
  const totalTrades   = filtered.length;
  const gagnants      = filtered.filter(t => t.resultat === 'Gagnant').length;
  const perdants      = filtered.filter(t => t.resultat === 'Perdant').length;
  const pnlTotal      = filtered.reduce((s, t) => s + (t.pnlUsd || 0), 0);
  const totalInvesti  = filtered.reduce((s, t) => s + (t.achatUsd || 0), 0);
  const roi           = totalInvesti > 0 ? (pnlTotal / totalInvesti) * 100 : 0;
  const winRate       = totalTrades > 0 ? (gagnants / totalTrades) * 100 : 0;
  const gainMoy       = gagnants > 0 ? filtered.filter(t=>t.resultat==='Gagnant').reduce((s,t)=>s+(t.pnlUsd||0),0) / gagnants : 0;
  const perteMoy      = perdants > 0 ? Math.abs(filtered.filter(t=>t.resultat==='Perdant').reduce((s,t)=>s+(t.pnlUsd||0),0) / perdants) : 0;

  const fmtUsd = (n) => {
    const sign = n >= 0 ? (n > 0 ? '+': '') : '-';
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  // Trier les trades
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'date')    return (b.entreeTs||0) - (a.entreeTs||0);
    if (sortBy === 'pnl')     return (b.pnlUsd||0) - (a.pnlUsd||0);
    if (sortBy === 'roi')     return (b.roi||0) - (a.roi||0);
    return 0;
  });

  // PNL cumulatif pour le graphique
  const pnlCurve = (() => {
    let cumul = 0;
    return [...filtered].sort((a,b) => (a.entreeTs||0)-(b.entreeTs||0)).map(t => {
      cumul += t.pnlUsd || 0;
      return { cumul, date: t.entreeTs ? new Date(t.entreeTs*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}) : ''};
    });
  })();

  // Export CSV
  const exportCSV = () => {
    const headers = ['Date','Token','Wallet Copié','Investi ($)','Vente ($)','PNL ($)','ROI (%)','Résultat'];
    const rows = sorted.map(t => [
      t.entreeTs ? new Date(t.entreeTs*1000).toLocaleDateString('fr-FR') : '',
      t.symbol || '',
      t.walletSuivi || '',
      (t.achatUsd || 0).toFixed(2),
      (t.venteUsd || 0).toFixed(2),
      (t.pnlUsd || 0).toFixed(2),
      t.roi !== undefined ? t.roi.toFixed(1) : '',
      t.resultat || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${new Date().toLocaleDateString('fr-FR').replace(/\//g,'-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Wallets les plus copiés
  const walletStats = {};
  filtered.forEach(t => {
    if (!t.walletSuivi) return;
    if (!walletStats[t.walletSuivi]) walletStats[t.walletSuivi] = { trades:0, pnl:0, wins:0, bestPnl:-Infinity, worstPnl:Infinity, invested:0 };
    walletStats[t.walletSuivi].trades++;
    walletStats[t.walletSuivi].pnl += t.pnlUsd || 0;
    walletStats[t.walletSuivi].invested += t.achatUsd || 0;
    if (t.resultat === 'Gagnant') walletStats[t.walletSuivi].wins++;
    const p = t.pnlUsd || 0;
    if (p > walletStats[t.walletSuivi].bestPnl) walletStats[t.walletSuivi].bestPnl = p;
    if (p < walletStats[t.walletSuivi].worstPnl) walletStats[t.walletSuivi].worstPnl = p;
  });
  const topWallets = Object.entries(walletStats).sort((a,b) => b[1].pnl - a[1].pnl);

  return (
    <div className="an-root">

      {/* Period filter */}
      <div className="an-period-bar">
        <div className="an-periods">
          {['7D','30D','90D','ALL'].map(p=>(
            <button key={p} className={`an-period ${period===p?'active':''}`} onClick={()=>setPeriod(p)}>{p}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:8,height:8,borderRadius:'50%',background:trades.length>0?'#00e5cc':'#3a4a5a',animation:trades.length>0?'pulse 1.5s infinite':'none'}}/>
          <span style={{fontSize:11,color:'#5a6a80'}}>{trades.length > 0 ? `${trades.length} trade(s) bot`: 'En attente du bot'}</span>
        </div>
      </div>

      {/* Stats cards */}
      <div className="an-stat-cards">
        <div className="an-stat-card">
          <div className="an-sc-icon">
            <svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M3 10h14M10 3l7 7-7 7"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
          </div>
          <div className="an-sc-body">
            <div className="an-sc-label">TRADES EXÉCUTÉS</div>
            <div className="an-sc-val">{totalTrades}</div>
            <div className="an-sc-sub">{gagnants} gagnants · {perdants} perdants</div>
          </div>
        </div>
        <div className="an-stat-card">
          <div className="an-sc-icon success">
            <svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M4 10l4 4 8-8"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
          </div>
          <div className="an-sc-body">
            <div className="an-sc-label">PNL TOTAL BOT</div>
            <div className="an-sc-val"style={{color:pnlTotal>=0?'#00e5cc':'#ff6b6b'}}>{totalTrades>0?fmtUsd(pnlTotal):'—'}</div>
            <div className="an-sc-sub">Investi: {totalInvesti>0?fmtUsd(totalInvesti):'—'}</div>
          </div>
        </div>
        <div className="an-stat-card">
          <div className="an-sc-icon pnl">
            <svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="10"width="3"height="8"rx=".5"fill="#00e5cc"/><rect x="7"y="6"width="3"height="12"rx=".5"fill="#00e5cc"opacity=".7"/><rect x="12"y="3"width="3"height="15"rx=".5"fill="#00e5cc"opacity=".5"/></svg>
          </div>
          <div className="an-sc-body">
            <div className="an-sc-label">ROI BOT</div>
            <div className="an-sc-val"style={{color:roi>=0?'#00e5cc':'#ff6b6b'}}>{totalTrades>0?`${roi>=0?'+':''}${roi.toFixed(1)}%`:'—'}</div>
            <div className="an-sc-sub">Sur capital investi</div>
          </div>
        </div>
        <div className="an-stat-card">
          <div className="an-sc-icon pos">
            <svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="4"width="16"height="12"rx="2"stroke="#f0c040"strokeWidth="1.4"/><rect x="6"y="8"width="8"height="4"rx="1"fill="#f0c040"opacity=".5"/></svg>
          </div>
          <div className="an-sc-body">
            <div className="an-sc-label">WIN RATE BOT</div>
            <div className="an-sc-val yellow">{totalTrades>0?`${winRate.toFixed(1)}%`:'—'}</div>
            <div className="an-sc-sub">{totalTrades>0?`Gain moy: ${fmtUsd(gainMoy)}`:'Aucun trade'}</div>
          </div>
        </div>
      </div>

      {/* Etat vide */}
      {totalTrades === 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'60px 20px',textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:48,marginBottom:16}}></div>
          <div style={{fontSize:18,fontWeight:700,color:'#e0e4ec',marginBottom:8}}>En attente des trades du bot</div>
          <div style={{fontSize:13,color:'#5a6a80',maxWidth:400,margin:'0 auto',lineHeight:1.7}}>
            Une fois le bot de trading configuré et actif, ses performances apparaîtront ici en temps réel.
          </div>
          <div style={{marginTop:20,display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
            {[['','Trades automatiques','Le bot copie les wallets suivis'],['','Analytics en temps réel','PNL, ROI, Win Rate mis à jour'],['','Alertes Telegram','Notification à chaque trade']].map(([icon,title,desc])=>(
              <div key={title} style={{background:'#0a0d14',border:'1px solid #1a2030',borderRadius:8,padding:'16px 20px',maxWidth:200,textAlign:'center'}}>
                <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:4}}>{title}</div>
                <div style={{fontSize:11,color:'#5a6a80'}}>{desc}</div>
              </div>
            ))}
          </div>

          {/*  Bouton pour ajouter un trade test */}
          <button onClick={()=>{
            const testTrade = {
              id: Date.now(),
              symbol: 'TEST',
              mint: 'test',
              entreeTs: Math.floor(Date.now()/1000) - 3600,
              sortieTs: Math.floor(Date.now()/1000),
              achatUsd: 100,
              venteUsd: 145,
              pnlUsd: 45,
              roi: 45,
              roiPos: true,
              resultat: 'Gagnant',
              walletSuivi: 'TestWallet',
              txHash: 'test_tx',
            };
            const existing = loadBotTrades();
            localStorage.setItem('bot-trades', JSON.stringify([...existing, testTrade]));
            setTrades(loadBotTrades());
          }} style={{marginTop:20,padding:'10px 24px',borderRadius:7,background:'rgba(0,229,204,0.12)',border:'1px solid #00e5cc44',color:'#00e5cc',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            + Ajouter un trade test
          </button>
        </div>
      )}

      {/* Graphique PNL cumulatif */}
      {totalTrades > 0 && pnlCurve.length > 1 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px',marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:12,display:'flex',alignItems:'center',gap:7}}>
            <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M2 10l3-4 3 2 4-6"stroke="#00e5cc"strokeWidth="1.3"strokeLinecap="round"strokeLinejoin="round"/></svg>
            Évolution du PNL Bot
          </div>
          {(() => {
            const W=560,H=100,PAD=10;
            const vals=pnlCurve.map(p=>p.cumul);
            const minV=Math.min(...vals,0),maxV=Math.max(...vals,0);
            const range=maxV-minV||1;
            const x=(i)=>PAD+(i/(pnlCurve.length-1))*(W-PAD*2);
            const y=(v)=>PAD+(1-(v-minV)/range)*(H-PAD*2);
            const path=pnlCurve.map((p,i)=>`${i===0?'M':'L'}${x(i).toFixed(1)},${y(p.cumul).toFixed(1)}`).join('');
            const fill=path+`L${x(pnlCurve.length-1).toFixed(1)},${H-PAD} L${PAD},${H-PAD} Z`;
            const last=vals[vals.length-1];
            const col=last>=0?'#00e5cc':'#ff6b6b';
            const zeroY=y(0);
            return (
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:100}}>
                {zeroY>PAD&&zeroY<H-PAD&&<line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY} stroke="#1a2030"strokeWidth="1"strokeDasharray="4,3"/>}
                <path d={fill} fill={last>=0?'rgba(0,229,204,0.07)':'rgba(255,107,107,0.07)'}/>
                <path d={path} fill="none"stroke={col} strokeWidth="1.5"strokeLinecap="round"strokeLinejoin="round"/>
                <circle cx={x(pnlCurve.length-1)} cy={y(last)} r="3"fill={col}/>
              </svg>
            );
          })()}
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#3a4a5a',marginTop:4}}>
            <span>{pnlCurve[0]?.date}</span>
            <span style={{color:pnlTotal>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>PNL cumulé: {fmtUsd(pnlTotal)}</span>
            <span>{pnlCurve[pnlCurve.length-1]?.date}</span>
          </div>
        </div>
      )}

      {/* Stats détaillées + Top wallets */}
      {totalTrades > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

          {/* Stats détaillées */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#00e5cc"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h4M4 9.5h3"stroke="#00e5cc"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Résumé Bot
            </div>
            {[
              ['Trades gagnants', `${gagnants} (${winRate.toFixed(1)}%)`, gagnants > 0 ? 'pos': ''],
              ['Trades perdants', `${perdants}`, perdants > 0 ? 'neg': ''],
              ['Gain moyen', gainMoy > 0 ? fmtUsd(gainMoy) : '—', 'pos'],
              ['Perte moyenne', perteMoy > 0 ? fmtUsd(perteMoy) : '—', 'neg'],
              ['Capital total investi', totalInvesti > 0 ? fmtUsd(totalInvesti) : '—', ''],
              ['PNL net', fmtUsd(pnlTotal), pnlTotal >= 0 ? 'pos': 'neg'],
            ].map(([label, val, cls]) => (
              <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #0d1117',fontSize:12}}>
                <span style={{color:'#5a6a80'}}>{label}</span>
                <span style={{fontWeight:700,color:cls==='pos'?'#00e5cc':cls==='neg'?'#ff6b6b':'#c0c8d8'}}>{val}</span>
              </div>
            ))}
          </div>

          {/* Stats par wallet copié */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M7 1l1.5 3.5H12l-2.8 2 1.1 3.5L7 8l-3.3 2 1.1-3.5L2 4.5h3.5z"fill="#f0c040"opacity=".8"/></svg>
              Stats par Wallet Copié
            </div>
            {topWallets.length === 0 ? (
              <div style={{textAlign:'center',padding:'30px 0',color:'#3a4a5a',fontSize:12}}>Aucun wallet copié</div>
            ) : (
              <div style={{overflowY:'auto',maxHeight:260}}>
                {topWallets.map(([wallet, stats], i) => {
                  const winPct = stats.trades > 0 ? ((stats.wins/stats.trades)*100).toFixed(0) : 0;
                  const roi = stats.invested > 0 ? ((stats.pnl/stats.invested)*100).toFixed(1) : 0;
                  return (
                    <div key={wallet} style={{padding:'10px 0',borderBottom:'1px solid #0d1117'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                        <span style={{fontSize:12,flexShrink:0}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span>
                        <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8',fontFamily:'monospace',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {wallet.length>16?`${wallet.slice(0,6)}…${wallet.slice(-4)}`:wallet}
                        </span>
                        <span style={{fontSize:13,fontWeight:700,color:stats.pnl>=0?'#00e5cc':'#ff6b6b',flexShrink:0}}>{fmtUsd(stats.pnl)}</span>
                      </div>
                      <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                        <span style={{fontSize:10,color:'#5a6a80'}}>{stats.trades} trades</span>
                        <span style={{fontSize:10,fontWeight:600,color:Number(winPct)>=50?'#00e5cc':'#ff6b6b'}}>{winPct}% win</span>
                        <span style={{fontSize:10,color:'#5a6a80'}}>ROI <span style={{color:Number(roi)>=0?'#7c4dff':'#ff6b6b',fontWeight:600}}>{Number(roi)>0?'+':''}{roi}%</span></span>
                        {stats.bestPnl > -Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↑ <span style={{color:'#00e5cc'}}>{fmtUsd(stats.bestPnl)}</span></span>}
                        {stats.worstPnl < Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↓ <span style={{color:'#ff6b6b'}}>{fmtUsd(stats.worstPnl)}</span></span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historique des trades */}
      {totalTrades > 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#7c4dff"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h6M4 9.5h4"stroke="#7c4dff"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Historique des Trades Bot
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <button onClick={exportCSV} style={{padding:'4px 12px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc33',color:'#00e5cc',display:'flex',alignItems:'center',gap:5}}>
                <svg viewBox="0 0 12 12"fill="none"width="10"height="10"><path d="M6 1v7M3 5l3 3 3-3M1 10h10"stroke="currentColor"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
                CSV
              </button>
              <div style={{width:1,height:14,background:'#1a2030'}}/>
              {[['date','Date'],['pnl','PNL'],['roi','ROI']].map(([k,l])=>(
                <button key={k} onClick={()=>setSortBy(k)} style={{padding:'4px 10px',borderRadius:4,fontSize:10,fontWeight:600,cursor:'pointer',background:sortBy===k?'rgba(124,77,255,0.15)':'transparent',border:`1px solid ${sortBy===k?'#7c4dff44':'#1a2030'}`,color:sortBy===k?'#7c4dff':'#5a6a80'}}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1a2030'}}>
                  {['DATE','TOKEN','WALLET COPIÉ','INVESTI','PNL','ROI','RÉSULTAT'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#3a4a5a',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((t,i)=>(
                  <tr key={t.id||i} style={{borderBottom:'1px solid #0d1117'}}>
                    <td style={{padding:'10px 12px',fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>
                      {t.entreeTs?new Date(t.entreeTs*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit'}):'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{fontSize:12,fontWeight:700,color:'#e0e4ec'}}>{t.symbol||'—'}</div>
                    </td>
                    <td style={{padding:'10px 12px',fontSize:11,color:'#7c4dff',fontFamily:'monospace'}}>
                      {t.walletSuivi?`${t.walletSuivi.slice(0,6)}…${t.walletSuivi.slice(-4)}`:'—'}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,color:'#c0c8d8'}}>
                      {t.achatUsd>=1000?`$${(t.achatUsd/1000).toFixed(1)}K`:`$${(t.achatUsd||0).toFixed(0)}`}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {fmtUsd(t.pnlUsd||0)}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.roi||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {t.roi!==undefined?`${t.roi>=0?'+':''}${t.roi.toFixed(1)}%`:'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <span style={{padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:700,background:t.resultat==='Gagnant'?'rgba(0,229,204,0.12)':'rgba(255,107,107,0.12)',color:t.resultat==='Gagnant'?'#00e5cc':'#ff6b6b',border:`1px solid ${t.resultat==='Gagnant'?'#00e5cc33':'#ff6b6b33'}`}}>
                        {t.resultat||'—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}